import { BaseSearchStore, BaseClientSectionSearchController, BaseScreenSectionSearchController, SearchResultTypes, screenSearchResult, screenSearchData } from '../../../shared-solo/stores/SearchStore/SearchStore'
import { $RootStore } from '../RootStore'
import analytics from '../analytics'
import { SearchExternalClientsQuery } from '../../../graphQL/generated/client'
import { action, computed, observable, runInAction } from 'mobx'
import { FuseResult } from 'fuse.js'

export class SearchStore extends BaseSearchStore {
  @observable accessor _recentSearches: screenSearchResult[] = []

  constructor(rootStore: $RootStore) {
    super(rootStore)
    this.sections = ['SCREENS', 'CLIENTS']
    runInAction(() => {
      this.searchControllers.push(new ClientSectionSearchController('CLIENTS', rootStore))
      this.searchControllers.push(new ScreenSectionSearchController('SCREENS', rootStore))
    })
  }

  @action serialize = () => {
    return {
      recentSearches: this._recentSearches,
    }
  }

  @action inflate = (data: any) => {
    if (data?.recentSearches) {
      this._recentSearches = data.recentSearches
    }
  }

  @computed get recentSearches() {
    return this._recentSearches
  }

  private setRecentSearches(searches: screenSearchResult[]) {
    this._recentSearches = searches
    this.rootStore.storeDataSync.saveStoreToStorage('SEARCH')
  }

  @action clearData = () => {
    this._searchVisible = false
    this._searchTerm = ''
    this.setRecentSearches([])
  }

  @action showSearch = () => {
    this.triggerSearches()
    this._searchVisible = true
  }

  dismissSearch = () => {
    this.hideSearch()
  }

  trackSearch = () => {
    if (this.searchTerm.length >= 3) {
      analytics.track('General Search', {
        searchTerm: this.searchTerm,
        ...this.resultCounts,
      })
    }
  }

  @action handleClick = (item: screenSearchResult, navigate: (path: string) => void) => {
    this.dismissSearch()

    const MAX_RECENT_SEARCHES = 5
    const recentSearches = [...this.recentSearches]
    const existingIndex = recentSearches.findIndex(search => search.primaryTerm === item.primaryTerm)

    if (existingIndex !== -1) {
      const [existingSearch] = recentSearches.splice(existingIndex, 1)
      recentSearches.unshift(existingSearch)
    } else {
      if (recentSearches.length >= MAX_RECENT_SEARCHES) {
        recentSearches.pop()
      }
      recentSearches.unshift(item)
    }
    this.setRecentSearches(recentSearches)

    switch (item?.type) {
      case SearchResultTypes.Screen:
      case SearchResultTypes.Client:
        navigate(item?.address.path)
        break
    }
  }
}

export class ClientSectionSearchController extends BaseClientSectionSearchController {
  // Every client result deep-links into /clients, so there's nothing to offer an audience that
  // can't open it. Returning before setting loading (rather than emptying the results after
  // fetching) keeps the section out of the modal entirely instead of flashing a skeleton.
  @action search = async () => {
    if (!this.store.canAccessFeature('clients')) {
      this.loading = false
      this.results = []
      return this.results
    }

    this.loading = true
    this.results = []
    this.error = {}
    this.debounced_fetch()
    return this.results
  }

  formatExternalClientSearchResult = (clients: SearchExternalClientsQuery['searchExternalClients']) => {
    if (!clients) return []
    return clients.filter(Boolean).map(client => ({
      item: {
        primaryTerm: `${client.firstName} ${client.lastName}`,
        address: {
          path: `/clients/list/${client._id.toString()}/view/info`,
        },
        icon: 'client',
        type: SearchResultTypes.Client,
        color: client.color,
      },
    }))
  }
}

export class ScreenSectionSearchController extends BaseScreenSectionSearchController {
  @computed get tapToPayScreenData() {
    return []
  }

  protected canReach = (item: any): boolean => {
    const path: string = item?.address?.desktopPath ?? ''

    if (!path || path.includes('://')) return true

    return this.store.canAccessRoute(path)
  }

  formatScreenSearchResult = (result: FuseResult<screenSearchData>): FuseResult<screenSearchResult> => {
    const { item, ...fuseResultRest } = result
    const { address: { desktopPath }, desktopIcon, ...rest } = item
    return {
      ...fuseResultRest,
      item: {
        ...rest,
        address: {
          path: desktopPath,
          params: {},
        },
        icon: desktopIcon,
        type: SearchResultTypes.Screen,
      },
    }
  }
}